import useReveal from '../hooks/useReveal';

/**
 * AboutMe — the human moment before the contact CTA.
 *
 * Small-business owners hire people, not studios. The site was
 * missing a face and a story, and that gap was doing more damage
 * to conversion than any visual polish could fix.
 *
 * Placement is deliberate: it sits between Proof (commitments) and
 * Finale (contact). The arc now reads as problem → work → process →
 * standards → person → call. The person is the softener before the ask.
 *
 * Bio philosophy: lead with credentials that signal trust at this
 * price point (VT alum, full-stack at a global consulting firm,
 * Fortune 500 client work) — but never name-drop the firm. Saying
 * "global consulting firm" with "Fortune 500 clients" lets the reader
 * fill in the picture themselves, which lands harder than naming the
 * brand and risking a "they're just sales-channel-mining their old
 * employer" smell. Then pivot to the studio thesis: same rigor,
 * applied to small-business work.
 *
 * The photo is served from `public/brand/about-me-420.jpg` (1x) +
 * `about-me-840.jpg` (2x), generated from a square crop of the v2
 * portrait shot.
 */
export default function AboutMe() {
  const figRef = useReveal<HTMLDivElement>();
  const bodyRef = useReveal<HTMLDivElement>();

  return (
    <section id="about" className="section about">
      <div className="wrap about-grid">
        <div ref={figRef} className="about-photo reveal">
          {/* Decorative halo behind the portrait picks up the brass
              gradient so the person and the brand share a single
              light source. */}
          <div className="about-photo-halo" aria-hidden="true" />
          <img
            src="/brand/about-me-420.jpg"
            srcSet="/brand/about-me-420.jpg 1x, /brand/about-me-840.jpg 2x"
            alt="Dillon, founder of WebNova"
            width={420}
            height={420}
            loading="lazy"
            decoding="async"
          />
        </div>

        <div ref={bodyRef} className="about-copy reveal d1">
          <p className="eyebrow">
            <span className="dot" aria-hidden="true" />
            About
          </p>
          <h2>
            Hi, I’m Dillon. <em className="serif">WebNova is me.</em>
          </h2>
          <p>
            I grew up in Ashburn and studied Business Information Technology at Virginia&nbsp;Tech.
            After graduating, I spent several years as a full-stack engineer at a global consulting
            firm, building and maintaining enterprise software for Fortune&nbsp;500 companies.
          </p>
          <p>
            That work taught me how to build systems properly — structured, scalable, and built to
            last. But it also made something obvious: most small-business websites are treated like
            an afterthought, even when the business itself isn’t.
          </p>
          <p className="about-pivot">WebNova exists to fix that.</p>
          <p>
            I take the same level of care, clarity, and engineering that goes into enterprise
            software and apply it to the businesses people actually interact with every day — the
            dentist, the restaurant, the local shop. No templates. No handoffs. Every site is
            designed and built end-to-end by the person you’re working with.
          </p>
          <p>And when the site goes live, I’m still just one call away.</p>
          <p className="about-meta">
            <span>Ashburn, Virginia</span>
            <span aria-hidden="true"> · </span>
            <span>Working with businesses anywhere</span>
          </p>
        </div>
      </div>
    </section>
  );
}
